import { UseQueryResult } from "@dashboard/hooks/makeQuery";
import React from "react";

interface Connection {
  edges: readonly unknown[];
  pageInfo: { hasNextPage: boolean; endCursor?: string | null };
}

export interface AllPagesResult {
  /** True while pages beyond the first are still being fetched. */
  paging: boolean;
  /** A page past the first failed to load (or the list never settled). */
  error: boolean;
  /** Clears the error and walks the remaining pages again. */
  retry: () => void;
}

/**
 * How many times one cursor may be requested before the walk gives up. A
 * cursor is requested again only when it comes back as the current end
 * cursor over different data, which is page 1 re-arriving over the merged
 * list (Apollo 3.4's fetchMore ends with a reobserve that, under
 * cache-and-network, fetches page 1 again and overwrites the merged list
 * when page 1 changed). The callers also set `nextFetchPolicy: "cache-first"`
 * so that reobserve reads the cache; this cap is what keeps a list that
 * keeps changing from walking forever.
 */
export const MAX_REQUESTS_PER_CURSOR = 3;

/**
 * Keeps calling loadMore (fetchMore with the query's own variables) on a
 * `first: 100, after: $after` query until its connection says there is no
 * next page, so a vocabulary longer than one page is never cut off.
 * `connection` picks the connection out of the data; `merge` appends a
 * fetched page to what the query already holds.
 */
export const useAllPages = <TData, TVariables>(
  query: Pick<UseQueryResult<TData, TVariables>, "data" | "loadMore">,
  connection: (data: TData | undefined) => Connection | null | undefined,
  merge: (held: TData, page: TData) => TData,
): AllPagesResult => {
  const [error, setError] = React.useState(false);
  // One page at a time: two fetches in flight would both append.
  const [inFlight, setInFlight] = React.useState(false);
  const pageInfo = connection(query.data)?.pageInfo;
  const endCursor = pageInfo?.hasNextPage ? (pageInfo.endCursor ?? null) : null;
  // Refs, so a new loadMore or merge function per render cannot re-fire the
  // effect. `requested` holds, per cursor, the data it was requested over
  // and how often: the same cursor over the same data is never fetched twice
  // (a double-run effect would append the same page twice), while the same
  // cursor over new data is the list having been reset under us.
  const loadMoreRef = React.useRef(query.loadMore);
  const mergeRef = React.useRef(merge);
  const requested = React.useRef(new Map<string, { data: unknown; count: number }>());
  const mounted = React.useRef(true);

  loadMoreRef.current = query.loadMore;
  mergeRef.current = merge;

  React.useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (!endCursor || error || inFlight) {
      return;
    }

    const seen = requested.current.get(endCursor);

    if (seen && seen.data === query.data) {
      return;
    }

    if (seen && seen.count >= MAX_REQUESTS_PER_CURSOR) {
      setError(true);

      return;
    }

    requested.current.set(endCursor, { data: query.data, count: (seen?.count ?? 0) + 1 });
    setInFlight(true);

    // The dashboard's queries run with errorPolicy "all", so a failed page
    // can resolve with errors instead of rejecting.
    loadMoreRef
      .current((held, page) => mergeRef.current(held, page), {
        after: endCursor,
      } as unknown as Partial<TVariables>)
      .then(result => {
        if (mounted.current && (result.error || (result.errors?.length ?? 0) > 0)) {
          setError(true);
        }
      })
      .catch(() => {
        if (mounted.current) {
          setError(true);
        }
      })
      .finally(() => {
        if (mounted.current) {
          setInFlight(false);
        }
      });
    // A merged page moves endCursor on (or changes the data), which re-runs this.
  }, [endCursor, error, inFlight, query.data]);

  const retry = React.useCallback(() => {
    requested.current.clear();
    setError(false);
  }, []);

  return { paging: !!endCursor && !error, error, retry };
};
